import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { onSaveLimitDetails } from "../../Store/Slices/cardDataSlice";

const clientType = process.env.REACT_APP_CLIENT_TYPE;
const SECRET_KEY = process.env.REACT_APP_PK;
const ACCESS_KEY = process.env.REACT_APP_E_K;

const VTIGER_ENCRYPTION_KEY = "qGbJ7G7G87SBOrOXihegtXjQ";

const VTIGER_RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA2K2OXmCc9hP4kioRqKKoQ5m+OZm4MmCzR3+tOUeJ3WtsPYfNGSEulfnNtXbJVtqZP8idT1egxBmpr+BI/j9itcE7NZqKvHWUCcAXv/d/fwo2M4YXXYQlJmda5y3PC69EI+rNDcElh+Lktpquq5bP3DffYKW76kH+LDgYDwNEtFTPk3uXftiYmF4j3PEDwb6TkPIHXIRQ9hx7T0br49AEVzUNKGCDyqJWo1VGvxbd5F7MH+3Gq+7vW+TaiCPiLhAjjS7GxTItmKXShHCtH8c55R34MPVQo69Ja+CVu1p/tyC/PRwRuC4CalyguUGsMbo53tpGyuHM9oqsW5edHVsbUwIDAQAB-----END PUBLIC KEY-----`;
const GetImageUrl = (imagePath) => {
  return `${process.env.REACT_APP_CLIENT_URL}${imagePath}`;
};
const ClientInfo = () => {
  return useSelector((state) => state.clientDataReducer);
};

const useGaTrackingConfig = () => {
  const clientConfigurationData = useSelector(
    (state) => state.clientConfigurationReducer.clientConfigurationData?.[0]
  );
  const gaConfigMap = useMemo(() => {
    const configList = Array.isArray(clientConfigurationData?.clientConfig)
      ? clientConfigurationData.clientConfig
      : [];

    return configList.reduce((acc, item) => {
      if (item?.configKey) {
        acc[item.configKey] = item.configValue || "";
      }
      return acc;
    }, {});
  }, [clientConfigurationData?.clientConfig]);

  const getGaValue = useCallback(
    (configKey) => gaConfigMap[configKey] || "",
    [gaConfigMap]
  );

  const trackingContext = useMemo(
    () => ({
      client_code: getGaValue("gaClientCode"),
      platform_code: getGaValue("gaPlatformCode"),
      affiliation: getGaValue("gaAffiliation"),
    }),
    [getGaValue]
  );

  return {
    gaClientCode: getGaValue("gaClientCode"),
    gaPlatformCode: getGaValue("gaPlatformCode"),
    gaAffiliation: getGaValue("gaAffiliation"),
    trackingContext,
    getGaValue,
  };
};

/* Reads a client configuration value by key as a trimmed string.
   Config keys live in one of two records depending on where they were saved:
   the client master (clientConfigurations, from client-master-by-Id) or the
   client configuration (clientConfig, from client-configuration). Both are
   checked so the key works either way.
   Returns "" when the key is absent or blank. Use this for free text such as
   URLs; getConfigValue is not interchangeable because it JSON parses the value. */
const useClientConfigValue = (configKey) => {
  const clientMasterConfigurations = useSelector(
    (state) => state.clientDataReducer?.clientData?.clientConfigurations
  );
  const clientConfiguration = useSelector(
    (state) => state.clientConfigurationReducer?.clientConfigurationData?.[0]
  );

  const key = String(configKey ?? "").trim().toLowerCase();
  const valueFrom = (list) =>
    Array.isArray(list)
      ? list.find(
        (item) => String(item?.configKey ?? "").trim().toLowerCase() === key
      )?.configValue
      : undefined;

  const value =
    valueFrom(clientMasterConfigurations) ??
    valueFrom(clientConfiguration?.clientConfig);

  return String(value ?? "").trim();
};

/* Reads a client configuration flag by key.
   Treats true, 1, "1", "true" and "active" as enabled. Anything else,
   including a missing key, is false. */
const useClientConfigFlag = (configKey) =>
  ["1", "true", "active"].includes(
    useClientConfigValue(configKey).toLowerCase()
  );

const Dispatch = () => useDispatch();
const SupplierCapping = () => {
  return useSelector(
    (state) =>
      state.clientCappingReducer?.clientCappingData?.find(
        (findData) => findData?.enabled
      )?.upperCapping
  );
};

const MaxQuan = () => {
  return useSelector(
    (state) => state.clientDataReducer?.clientData?.overallCapping
  );
};

const TransactionLimit = () => {
  return useSelector(
    (state) => state.clientDataReducer?.clientData
  );
};

const fillTemplate = (template, values) => {
  return template.replace(/{{(.*?)}}/g, (_, key) => values[key.trim()] ?? "");
};

const showMaxMessg = (temp, maxQuantity, supCap, price) => {
  const values = { maxQuantity, price };
  const message = fillTemplate(temp, values);
  toast.error(message);
};

const ThemeData = () => {
  return useSelector((state) => state.themeSliceReducer?.getThemeData?.[0]);
};
const isValidColor = (value) => {
  const s = new Option().style;
  s.color = value;
  return s.color !== "";
};
function hexToRgbaGradient(hex) {
  if (!hex || typeof hex !== "string") return "";

  // expand shorthand hex (#abc => #aabbcc)
  if (hex.length === 4) {
    hex = "#" + [...hex.slice(1)].map((c) => c + c).join("");
  }

  const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return "";

  const r = parseInt(match[1], 16);
  const g = parseInt(match[2], 16);
  const b = parseInt(match[3], 16);

  return `linear-gradient(to right, rgba(${r}, ${g}, ${b}, 1) 0%, rgba(${r}, ${g}, ${b}, 0.95) 50%, rgba(${r}, ${g}, ${b}, 0.8) 100%)`;
}

const ApplyThemeStyles = (themeData) => {
  const setStyle = (variable, color, fallback) => {
    const value =
      typeof color === "string" &&
        (color.startsWith("linear-gradient") || isValidColor(color))
        ? color
        : fallback;

    document.documentElement.style.setProperty(variable, value);
  };
  setStyle(
    "--card-heading-color",
    themeData?.primaryTextColorHeading,
    "#222222"
  );
  setStyle(
    "--shop-category-below-color",
    themeData?.secondaryTextColorHeading,
    "#515151"
  );
  setStyle(
    "--select-button-bg",
    themeData?.buttonWithBGSecondaryBgColor
      ? hexToRgbaGradient(themeData.buttonWithBGSecondaryBgColor)
      : "",
    "linear-gradient(to right, rgba(14, 77, 141, 1) 0%, rgba(14, 77, 141, 0.95) 50%, rgba(14, 77, 141, 0.8) 100%)"
  );

  setStyle(
    "--text-select-color",
    themeData?.buttonWithBGSecondaryTextColor,
    "#ffffff"
  );

  setStyle("--explore-back-coloer", themeData?.secondaryBgColor, "#20f2932");
  setStyle("--explore-back-coloer", themeData?.headerBorderColor, "#20f2932");
  setStyle("--explore-back-coloer", themeData?.bodyTextColor, "#20f2932");
  setStyle("--explore-back-coloer", themeData?.bodyBgColor, "#20f2932");
  setStyle(
    "--header-point-text-color1",
    themeData?.headerPointsTextColor,
    "#00477A"
  );
  setStyle("--header-point-bg", themeData?.headerPointsBgColor, "#eee");
  setStyle(
    "--username-text-color",
    themeData?.headerUserInitialsTextColor,
    "#565656"
  );
  setStyle("--initial-user-bg", themeData?.headerUserInitialsBgColor, "#eee");
  setStyle(
    "--header-point-star-color1",
    themeData?.pointsWithStarInBadgeTextColor,
    "#00477A"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.pointsWithStarInBadgeBgColor,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.pointColorNotInBadge,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBGPrimaryTextColor,
    "#20f2932"
  );
  setStyle(
    "--login-hover-text-color",
    themeData?.buttonWithBGPrimaryBorderColor,
    "#ffffff"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBGSecondaryTextColor,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBGSecondaryBorderColor,
    "#20f2932"
  );

  setStyle(
    "--retrive-btn-back-color",
    themeData?.buttonWithBorderPrimaryTextColor,
    "#0e4d8d"
  );
  setStyle(
    "--retrive-btn-border-color",
    themeData?.buttonWithBorderPrimaryBorderColor,
    "#0e4d8d"
  );
  setStyle(
    "--retrieve-bg-color",
    themeData?.buttonWithBorderPrimaryBgColor,
    "#ffffff"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBorderPrimaryOnHover,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBorderSecondaryTextColor,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBorderSecondaryBorderColor,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBorderSecondaryBgColor,
    "#20f2932"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.buttonWithBorderSecondaryOnHover,
    "#20f2932"
  );
  setStyle(
    "--trending-earn-upto-color",
    themeData?.textButtonWithBGPrimaryTextColor,
    "#787878"
  );
  setStyle(
    "--explore-back-coloer",
    themeData?.textButtonWithBGPrimaryOnHover,
    "#20f2932"
  );
  setStyle(
    "--login-button-color",
    themeData?.buttonWithBGPrimaryBgColor,
    "#0e4d8d"
  );
  setStyle(
    "--control-support",
    themeData?.textButtonWithBGSecondaryTextColor,
    "#0e4d8d"
  );
  setStyle(
    "--control-support-hov",
    themeData?.textButtonWithBGSecondaryOnHover,
    "#0e4d8d"
  );
  setStyle(
    "--cancel-button-text-color",
    themeData?.textButtonWithBGPrimaryBgColor,
    "#231f20"
  );
  setStyle(
    "--filter-checkbox-label-color",
    themeData?.formControlsTextColor,
    "#3a3a3a"
  );
  setStyle("--checkbox-color", themeData?.formControlsOnActionColor, "#0e4d8d");
  setStyle("--explore-back-coloer", themeData?.formControlsOnHover, "#20f2932");
  setStyle(
    "--card-starting-point-color",
    themeData?.giftCardStartingPriceTextColor,
    "linear-gradient(to right, #004d80, #007bb5)"
  );

  setStyle(
    "--card-earn-point-color",
    themeData?.giftCardEarnUpToPointTextColor,
    "linear-gradient(to right,#004d80,#007bb5)"
  );
  setStyle(
    "--denomination-price-color",
    themeData?.dinominationListTextColor,
    "#ffffff"
  );

  setStyle(
    "--explore-back-coloer",
    themeData?.dinominationBoxWithPriceImageUpload,
    "#20f2932"
  );
  setStyle(
    "--earn-points-text-color",
    themeData?.infoBadgePayWithPointsAndCashTextColor,
    "#222222"
  );
  setStyle(
    "--bg-white-2",
    themeData?.infoBadgePayWithPointsAndCashBGColor,
    "#F0ECEC"
  );
  setStyle(
    "--trending-earn-points-color",
    themeData?.buttonWithBGSecondaryOnHover,
    "#3a3a3a"
  );
  setStyle(
    "--footer-title-bg",
    themeData?.buttonWithBGPrimaryOnHover,
    "#1F222B"
  );
  /////////
  setStyle(
    "--explore-back-color",
    themeData?.promotionalStripBGColor,
    "#202932"
  );
  setStyle(
    "--explore-earn-text-color",
    themeData?.promotionalStripTextColor,
    "#ffffff"
  );
  setStyle(
    "--promotional-hover-text-color",
    themeData?.promotionalStripOnHover,
    "#fff"
  );
  setStyle("--bg-header", themeData?.headerBgColor, "#ffffff");
  setStyle(
    "--desktop-header-text-color",
    themeData?.headerTextColor,
    "#000000"
  );
  setStyle("--footer-bg-color", themeData?.footerBgColor, "#41444D");
  setStyle("--footer-text-color", themeData?.footerTextColor, "#ffffff");
  setStyle(
    "--login-text-color",
    themeData?.buttonWithBGPrimaryTextColor,
    "#ffffff"
  );
  setStyle(
    "--keep-bg-color",
    themeData?.thankYouCardKeepShoppingBGColor,
    "linear-gradient(86.41deg, #003B6B -26.81%, #0071B0 108.41%)"
  );
  setStyle(
    "--white-color-keep",
    themeData?.thankYouCardKeepShoppingTextColor,
    "#ffffff"
  );
  setStyle(
    "--thankyou-card-bg",
    themeData?.thankYouCardBackgroundColor,
    "#016DAB14"
  );
  setStyle("--getcode-text-color", themeData?.getCodeTextColor, "#ffffff");
  setStyle("--getcode-bg-color", themeData?.getCodeBgColor, "#0e4d8d");
  setStyle("--getcode-border-color", themeData?.getCodeBorderColor, "#0e4d8d");
  setStyle("--egiftcard-icon-color", themeData?.eGiftCardIconColor, "#016FD0");
  setStyle(
    "--disable-continue-color",
    themeData?.disabledContinueBtn,
    "#016DAB14"
  );
  setStyle("--show-more-bg-color", themeData?.moreGiftCardBgColor, "#ffffff");
  setStyle(
    "--redeemable-online-bg",
    themeData?.redeemableOnlineBgColor,
    " #F1FFEF"
  );
  setStyle(
    "--redeemable-offline-bg",
    themeData?.redeemableOfflineBgColor,
    "#E6F2FE"
  );
  setStyle(
    "--redeemable-both-bg",
    themeData?.redeemableOnlineOfflineBgColor,
    "#FBF7ED "
  );
  setStyle(
    "--redeemable-online-txt",
    themeData?.redeemableOnlineTxtColor,
    "#016FD0"
  );
  setStyle(
    "--redeemable-offline-txt",
    themeData?.redeemableOfflineTxtColor,
    "#037BF3"
  );
  setStyle(
    "--redeemable-both-txt",
    themeData?.redeemableOnlineOfflineTxtColor,
    "#C38C00"
  );
  setStyle("--active-dot-color", themeData?.bannerDotActiveColor, "#016FD0");
  // Generate dynamic SVG for slider and set as CSS var data URL
  const generateSliderSVG = (bgColor = "#8F9E5B", strokeColor = "#ffffff") => {
    const svgContent = `<svg width="57" height="36" viewBox="0 0 57 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="0.719897" y="0.676623" width="54.8325" height="34.6468" rx="17.3234" fill="${bgColor}"/>
<rect x="0.719897" y="0.676623" width="54.8325" height="34.6468" rx="17.3234" stroke="${bgColor}" stroke-width="1.35325"/>
<path d="M22.722 14.9546L22.722 21.7208" stroke="${strokeColor}" stroke-width="1.35325" stroke-linecap="round"/>
<path d="M28.1361 12.9253L28.1361 23.0747" stroke="${strokeColor}" stroke-width="1.35325" stroke-linecap="round"/>
<path d="M33.5502 14.9546L33.5502 21.7208" stroke="${strokeColor}" stroke-width="1.35325" stroke-linecap="round"/>
</svg>`;
    // Inline as data URL and assign to CSS var (works for pseudo elements)
    const encoded = encodeURIComponent(svgContent)
      .replace(/'/g, "%27")
      .replace(/\(/g, "%28")
      .replace(/\)/g, "%29");
    const dataUrl = `url("data:image/svg+xml,${encoded}")`;
    document.documentElement.style.setProperty("--slider-thumb-image", dataUrl);
  };

  // Call the function with your desired colors
  generateSliderSVG(themeData?.sliderBackgroundColor || "#8F9E5B", "#ffffff");

  // Setup dynamic range fill percentage and colors
  const setRangeFill = (input) => {
    if (!input) return;
    const min = Number(input.min || 0);
    const max = Number(input.max || 100);
    const val = Number(input.value || 0);
    const percent = ((val - min) * 100) / (max - min);
    input.style.setProperty("--slider-range-percent", `${percent}%`);
  };

  // Default fill and empty colors (theme-overridable)
  setStyle("--slider-range-fill", themeData?.sliderBackgroundColor, "#8F9E5B");
  setStyle("--slider-range-empty", themeData?.sliderDotLineColor, "#787878");

  // Attach listeners for all relevant range inputs
  const rangeInputs = document.querySelectorAll(
    '.filter-container input[type="range"], input[type="range"].rsBar'
  );
  rangeInputs.forEach((input) => {
    setRangeFill(input);
    input.addEventListener("input", () => setRangeFill(input));
    input.addEventListener("change", () => setRangeFill(input));
  });
  setStyle("--bg-voucher-2", themeData?.infoBadgeVoucherBGColor, "#F0ECEC");
  setStyle("--voucher-color", themeData?.infoBadgeVoucherTextColor, "#222222");
  setStyle(
    "--earn-text-color",
    themeData?.infoBadgePointsYouWillEarnTextColor,
    "#222222"
  );
  setStyle(
    "--light-blue-1",
    themeData?.infoBadgePointsYouWillEarnBGColor,
    "#D6E7F1"
  );
  setStyle("--redeem-text-color", themeData?.redeemText, "#0e4d8d");
  setStyle("--unlock-text-color", themeData?.unlockPointsTextColor, "#454545");
  setStyle("--light-grey-1", themeData?.unlockPointsBackgroundColor, "#E8EAEF");
  setStyle(
    "--denomination-box",
    themeData?.denominationBoxBackgroundColor,
    "#1B2A71"
  );
  setStyle(
    "--denomination-box-border",
    themeData?.denominationBoxBorderColor,
    "#016FD0"
  );
  setStyle("--denomination-earn-color", themeData?.earnPointColor, "#277D27");
  setStyle(
    "--plus-minus-bg",
    themeData?.plusMinusBackgroundColor,
    "rgba(236, 99, 43, 0.1)"
  );
  setStyle("--plus-minus-color", themeData?.plusMinusColor, "#EC632B");
  setStyle(
    "--giftcard-earpoint-color",
    themeData?.giftCardEarnPointColor,
    "#ffffff"
  );
  setStyle(
    "--giftcard-earpoint-bg",
    themeData?.giftCardEarnPointBackground,
    "#277D27"
  );
  setStyle("--giftcard-text-color", themeData?.giftCardTextColor, "#222222");
  setStyle("--light-green-bg", themeData?.giftCardBackgroundColor, "#016DAB14");
  setStyle("--gift-card-border", themeData?.giftCardBorderColor, "#E9E9E9");
  setStyle(
    "--product-info-color",
    themeData?.productInformationColor,
    "#212F52"
  );
  setStyle("--discount-bg-color", themeData?.discountBGColor, "#ffffffbf");
  setStyle(
    "--trending-border-color",
    themeData?.discountBorderColor,
    "#0000001a"
  );
  setStyle("--trending-off-color", themeData?.discountTextColor, "#fc511a");
  setStyle(
    "--term-condition-color",
    themeData?.termsConditionTextColor,
    "#787878"
  );
  setStyle(
    "--thank-paid-amount-bg",
    themeData?.thankyouCardBgColor,
    "#f5hyhf5f5"
  );
  setStyle(
    "--thank-page-amount-border",
    themeData?.thankyouCardBorderColor,
    "#d2dy2d280"
  );
  setStyle("--disable-button-color", themeData?.disableButtonText, "#ffffff");
  setStyle(
    "--disable-button-bg",
    themeData?.disableButtonBackground,
    "#016fd0"
  );
  setStyle(
    "--disable-btn-border-color",
    themeData?.disableButtonBorder,
    "#0e4d8d"
  );
  setStyle(
    "--OutOfStockTextColor",
    themeData?.stockOutTextColor,
    "#d61448"
  );
  setStyle(
    "--OutOfStockBgColor",
    themeData?.stockOutBgColor,
    "#fff"
  );
  setStyle(
    "--OutOfStockBorderColor",
    themeData?.stockOutBorderColor,
    "#d61448"
  );
  setStyle(
    "--stockOutMsgTextColor",
    themeData?.stockOutMsgTextColor,
    "#B71C1C"
  );
  setStyle(
    "--stockOutMsgBgColor",
    themeData?.stockOutMsgBgColor,
    "#FFF3F3"
  );
  setStyle(
    "--stockOutMsgBorderColor",
    themeData?.stockOutMsgBorderColor,
    "#B71C1C"
  );
  setStyle(
    "--stockOutIconColor",
    themeData?.stockOutIconColor,
    "#B71C1C"
  );
  // Card selection module theme variables
  setStyle(
    "--pointsTextColor",
    themeData?.pointsTextColor,
    "#00AE4E"
  );
  setStyle(
    "--checkbox-color",
    themeData?.redemptionFeesCheckboxColor,
    "#9D1D27"
  );
  setStyle(
    "--knowmore-text-color",
    themeData?.knowMoreTextColor,
    "#9D1D27"
  );
};
const V3Data = () => {
  return useSelector((state) => state.cardDataSliceReducer);
};
const LoginV3Data = () => {
  return useSelector((state) => state.validateLoginTokenSliceReducer.data);
};
const CardBasedRedemptionList = () => {
  return useSelector((state) => state?.validateLoginTokenSliceReducer?.data?.otherListAttribute);
};
const LoginReducerData = (client) => {
  return useSelector((state) => state.loginAuthReducer?.data?.[0]);
};

const OrderData = () => {
  return useSelector((state) => state.orderReducer);
};

const ParseInt = (str) => {
  if (typeof str !== "string") return NaN;
  return Number(str.replace(/,/g, ""));
};
const GetCurrentDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - 10);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};
const GenerateToken = () => {
  return useSelector((state) => state.generateTokenSliceReducer);
};
const EarnPoint = () => {
  return useSelector((state) => state.earnPointReducer);
};
const ApiRespCode = {
  v3Create: 200,
  success: 200,
  v3Success: "200",
  create: "201",
  update: "201",
  get: "200",
};
const VendorData = () => {
  return useSelector((state) => state.vendorOrderReducer);
};

const GetMemberData = () => {
  return useSelector((state) => state.getMemberSliceReducer);
};
const GetClientConfigData = () => {
  return useSelector((state) => state.clientConfigDataReducer);
};
const EmailData = () => {
  return useSelector((state) => state.sendMailReducer?.getMailData);
};
const SendSms = () => {
  return useSelector((state) => state.sendSmsReducer);
};
const SendMail = () => {
  return useSelector((state) => state.sendMailReducer);
};
const cleanHTML = (htmlString) => {
  return (
    htmlString && htmlString?.replace(/\s+/g, " ")?.replace(/"/g, "'")?.trim()
  );
};
const Navigate = () => useNavigate();
const CardDataSlice = () => {
  return useSelector((state) => state.cardDataSliceReducer);
};
const ClientConfigurationData = () => {
  return useSelector(
    (state) => state.clientConfigurationReducer.clientConfigurationData?.[0]
  );
};
const GetOrderCodeReducer = () => {
  return useSelector((state) => state.getOrderCodeReducer);
};
const RedeemPoint = () => {
  return useSelector((state) => state.redeemPointsReducer);
};
const UrlStoreReducer = () => {
  return useSelector((state) => state.urlStoreReducer);
};
const OnSwitchAccountSuccess = () => {
  return useSelector((state) => state.switchAccountReducer);
};
const TruncateName = (name, maxLength) => {
  if (!name || name.length <= maxLength) return name;

  const words = name.split(/[\s-]/);
  let result = "";

  for (let word of words) {
    if ((result + word).length <= maxLength) {
      result += (result ? " " : "") + word;
    } else {
      break;
    }
  }

  return result + "...";
};
const handleError = (text, duration = 5000) => {
  return toast.error(text, {
    autoClose: duration,
  });
};
const handleKeyDown = (e) => {
  if (e.key === "Enter") {
    return e.preventDefault();
  }
};
// init
const gtag = (...args) => {
  window.dataLayer.push(args);
};

// flexible tracker
export const trackEvent = (eventName, payload = {}) => {
  const data = {
    event: eventName,
    ...payload,
  };

  window.dataLayer = window.dataLayer || [];

  try {
    window.dataLayer.push({ items: null });
  } catch (e) {
    /* ignore */
  }
  window.dataLayer.push(data);
};

export const formatPrice = (amount) => {
  const num = Number(amount);

  if (isNaN(num)) return "0";
  const hasFraction = !Number.isInteger(num);

  return num.toLocaleString("en-IN", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

export const roundCurrency = (amount) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
};
const GetProdBudSumData = () => {
  return useSelector((state) => state.customerProductReducer);
};

function getWeekOfMonth(date) {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDayWeekday = (firstDay.getDay() + 6) % 7; // Monday = 0
  const adjustedDate = date.getDate() + firstDayWeekday;
  return Math.ceil(adjustedDate / 7);
}

function filterCurrentPeriodData(data) {
  const today = new Date();

  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();

  const dailyIdentifier = `${dd}${mm}${yyyy}`;
  const monthlyIdentifier = `${mm}${yyyy}`;
  const weekOfMonth = getWeekOfMonth(today);
  const weeklyIdentifier = `W${weekOfMonth}_${mm}${yyyy}`;

  return data.filter(
    (item) =>
      item.purchaseLimitIdentifier === dailyIdentifier ||
      item.purchaseLimitIdentifier === weeklyIdentifier ||
      item.purchaseLimitIdentifier === monthlyIdentifier
  );
}

const handleLimit = (payload, prodSumData, call, translation, limits) => {
  if (sessionStorage.getItem("login")) {
    const amount = Number(payload);

    // Merge prodSumData (used limits) with limits (allowed limits)
    const mergedData = limits?.map((limit) => {
      const used =
        Array.isArray(prodSumData) &&
        prodSumData?.find((p) => p.limitType === limit.limitType);
      return {
        limitType: limit.limitType,
        limitValue: Number(limit.limitValue),
        totalPurchasedValue: Number(used?.totalPurchasedValue || 0),
      };
    });

    const limitTypes = ["Daily", "Weekly", "Monthly", "Quarterly"];
    let limitBreached = null;

    for (const type of limitTypes) {
      const current = mergedData?.find((l) => l.limitType === type);
      if (!current) continue;

      const { limitValue, totalPurchasedValue } = current;
      const available = limitValue - totalPurchasedValue;

      // check if user exceeded this limit or available is 0
      if (amount > available || available === 0) {
        let reset_text = "";
        switch (type) {
          case "Daily":
            reset_text = translation?.next_day;
            break;
          case "Weekly":
            reset_text = translation?.next_week;
            break;
          case "Monthly":
            reset_text = translation?.next_month;
            break;
          case "Quarterly":
            reset_text = translation?.next_quarter;
            break;
          default:
            reset_text = "";
        }

        limitBreached = {
          amount: limitValue,
          timePeriod: type,
          disable: true,
          reset_text,
        };
        break;
      }
    }

    // If user exceeds any limit → disable and show info
    if (limitBreached) {
      call(onSaveLimitDetails(limitBreached));
    } else {
      // If user reduces amount within range → reset warning
      call(
        onSaveLimitDetails({
          amount: null,
          timePeriod: null,
          disable: false,
          reset_text: "",
        })
      );
    }
  } else {
    call(
      onSaveLimitDetails({
        amount: null,
        timePeriod: null,
        disable: false,
        reset_text: "",
      })
    );
  }
};

const getImageUrl = (url) => {
  if (!url) return "";
  return url.startsWith("http")
    ? url
    : `${process.env.REACT_APP_CLIENT_URL}${url}`;
};

const dynamicSuccessEmailColumnChnages = (emailTemplate, cardNumber, cardPin) => {
  let htmlStringData = emailTemplate;
  if (cardNumber && cardPin) {
    htmlStringData = htmlStringData?.replace(/{{#noGcId}}[\s\S]*?{{\/noGcId}}/, "");
    htmlStringData = htmlStringData?.replace(/{{#noVoucherCode}}[\s\S]*?{{\/noVoucherCode}}/, "");
    htmlStringData = htmlStringData
      ?.replace("{{#noGcId}}", "")
      ?.replace("{{/noGcId}}", "");
    htmlStringData = htmlStringData
      ?.replace("{{#noVoucherCode}}", "")
      ?.replace("{{/noVoucherCode}}", "");
    htmlStringData = htmlStringData
      ?.replace("{{#noFourCol}}", "")
      ?.replace("{{/noFourCol}}", "");
  }

  else if (!cardNumber) {
    htmlStringData = htmlStringData?.replace(/{{#noFourCol}}[\s\S]*?{{\/noFourCol}}/, "");
    htmlStringData = htmlStringData?.replace(/{{#noVoucherCode}}[\s\S]*?{{\/noVoucherCode}}/, "");
    htmlStringData = htmlStringData
      ?.replace("{{#noGcId}}", "")
      ?.replace("{{/noGcId}}", "");
  }
  else if (!cardPin) {
    htmlStringData = htmlStringData?.replace(/{{#noFourCol}}[\s\S]*?{{\/noFourCol}}/, "");
    htmlStringData = htmlStringData?.replace(/{{#noGcId}}[\s\S]*?{{\/noGcId}}/, "");
    htmlStringData = htmlStringData
      ?.replace("{{#noVoucherCode}}", "")
      ?.replace("{{/noVoucherCode}}", "");
  }
  return htmlStringData;
}

const getConfigValue = (configurations, key) => {
  const value = configurations?.find((item) => item.configKey === key)?.configValue;
  return value ? JSON.parse(value) : "";
};
const getValidDenominations = (denominations) => {
  const denoms = Array.isArray(denominations) ? denominations : [];
  return denoms?.map((item) => item?.denomination)
    .join(",");
};
// to calculate convenience fee and gst
const calculateConvenienceFeeGst = (
  denom,
  totalPaid,
  convenienceFee,
  gst,
) => {
  const perPointValue = denom / (totalPaid - (convenienceFee + gst));
  const denomWiseConvFee = +(convenienceFee * perPointValue);
  const denomWiseGst = +(gst * perPointValue);
  return denom + denomWiseConvFee + denomWiseGst;
};
// to calculate earn or burn points
const calculateEarnPoints = (dino_net_sale, net_sale, burn_or_earn) => {
  let bunrEarn = burn_or_earn;
  if (typeof burn_or_earn === "string") {
    bunrEarn = parseInt(bunrEarn.replace(/,/g, ""));
  }
  const result = (dino_net_sale / net_sale) * bunrEarn;
  return Math.round(result * 100) / 100;
};

const EARNING_DISPLAY_TYPE = Object.freeze({
  PERCENTAGE: "percentage",
  VALUE: "value",
});

const REWARD_JOURNEY_TYPE = Object.freeze({
  CARD: "card",
  WALLET: "wallet",
});

const REWARD_CONFIGURATION_SOURCE = Object.freeze({
  CARD_MASTER: "CARD_MASTER",
  LMS: "LMS",
  GLOBAL: "GLOBAL",
  SKU_CONVERSION: "SKU_CONVERSION",
});

const ROI_CONFIG_KEYS = Object.freeze({
  earn: [
    "earnRoi",
    "earnROI",
    "earn_roi",
    "earnPointValue",
    "earn_point_value",
  ],
  burn: [
    "burnRoi",
    "burnROI",
    "burn_roi",
    "burnPointValue",
    "burn_point_value",
  ],
});

// LMS cards and legacy Global configuration may expose one shared `roi` value.
// It applies to both Earn and Burn only after the dedicated value for that
// source is unavailable. `additionalroi` is intentionally not a fallback.
const SHARED_ROI_CONFIG_KEYS = Object.freeze(["roi"]);

const toRewardNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const isPositiveRewardNumber = (value) => toRewardNumber(value) > 0;

const getCaseInsensitiveValue = (source, key) => {
  if (!source || typeof source !== "object") return undefined;

  const matchKey = Object.keys(source).find(
    (sourceKey) => String(sourceKey).trim().toLowerCase() ===
      String(key).trim().toLowerCase(),
  );
  return matchKey ? source[matchKey] : undefined;
};

const getConfigListValue = (configurations, key) => {
  if (!Array.isArray(configurations)) return undefined;

  return configurations.find(
    (item) => String(item?.configKey ?? "").trim().toLowerCase() ===
      String(key).trim().toLowerCase(),
  )?.configValue;
};

const getFirstPositiveValueByKeys = (source, keys = []) => {
  if (!source || keys.length === 0) return 0;

  for (const key of keys) {
    const objectValue = getCaseInsensitiveValue(source, key);
    if (isPositiveRewardNumber(objectValue)) {
      return toRewardNumber(objectValue);
    }

    const listValue = getConfigListValue(source, key);
    if (isPositiveRewardNumber(listValue)) {
      return toRewardNumber(listValue);
    }
  }

  return 0;
};

const getFirstPositiveRewardConfigValue = (source, keys = []) => {
  const sources = [
    source,
    source?.clientConfig,
    source?.clientConfigurations,
    source?.configurations,
  ];

  for (const candidate of sources) {
    const value = getFirstPositiveValueByKeys(candidate, keys);
    if (value > 0) return value;
  }

  return 0;
};

const normalizeJourneyType = (journeyType) => {
  if (
    journeyType === true ||
    journeyType === 1 ||
    String(journeyType ?? "").trim().toLowerCase() === "wallet" ||
    String(journeyType ?? "").trim().toLowerCase() === "true" ||
    String(journeyType ?? "").trim() === "1"
  ) {
    return REWARD_JOURNEY_TYPE.WALLET;
  }

  return REWARD_JOURNEY_TYPE.CARD;
};

const resolveEarnRoiDetails = ({
  journeyType,
  cardMasterConfiguration,
  selectedCard,
  lmsConfiguration,
  globalConfiguration,
} = {}) => {
  const normalizedJourney = normalizeJourneyType(journeyType);

  if (normalizedJourney === REWARD_JOURNEY_TYPE.CARD) {
    const cardMasterRoi = getFirstPositiveRewardConfigValue(
      cardMasterConfiguration ?? selectedCard,
      ROI_CONFIG_KEYS.earn,
    );
    if (cardMasterRoi > 0) {
      return {
        value: cardMasterRoi,
        source: REWARD_CONFIGURATION_SOURCE.CARD_MASTER,
      };
    }

    const lmsRoi = getFirstPositiveRewardConfigValue(
      lmsConfiguration,
      [...ROI_CONFIG_KEYS.earn, ...SHARED_ROI_CONFIG_KEYS],
    );
    if (lmsRoi > 0) {
      return {
        value: lmsRoi,
        source: REWARD_CONFIGURATION_SOURCE.LMS,
      };
    }

    // Global and SKU configuration are intentionally ignored for card journeys.
    return { value: 0, source: null };
  }

  const globalRoi = getFirstPositiveRewardConfigValue(
    globalConfiguration,
    [...ROI_CONFIG_KEYS.earn, ...SHARED_ROI_CONFIG_KEYS],
  );
  return globalRoi > 0
    ? { value: globalRoi, source: REWARD_CONFIGURATION_SOURCE.GLOBAL }
    : { value: 0, source: null };
};

const resolveEarnRoi = (options = {}) => resolveEarnRoiDetails(options).value;

const resolveBurnRoiDetails = ({
  journeyType,
  cardMasterConfiguration,
  selectedCard,
  lmsConfiguration,
  globalConfiguration,
} = {}) => {
  const normalizedJourney = normalizeJourneyType(journeyType);

  if (normalizedJourney === REWARD_JOURNEY_TYPE.CARD) {
    const cardMasterRoi = getFirstPositiveRewardConfigValue(
      cardMasterConfiguration ?? selectedCard,
      ROI_CONFIG_KEYS.burn,
    );
    if (cardMasterRoi > 0) {
      return {
        value: cardMasterRoi,
        source: REWARD_CONFIGURATION_SOURCE.CARD_MASTER,
      };
    }

    const lmsRoi = getFirstPositiveRewardConfigValue(
      lmsConfiguration,
      [...ROI_CONFIG_KEYS.burn, ...SHARED_ROI_CONFIG_KEYS],
    );
    if (lmsRoi > 0) {
      return {
        value: lmsRoi,
        source: REWARD_CONFIGURATION_SOURCE.LMS,
      };
    }

    // Global and SKU configuration are intentionally ignored for card journeys.
    return { value: 0, source: null };
  }

  const globalRoi = getFirstPositiveRewardConfigValue(
    globalConfiguration,
    [...ROI_CONFIG_KEYS.burn, ...SHARED_ROI_CONFIG_KEYS],
  );
  return globalRoi > 0
    ? { value: globalRoi, source: REWARD_CONFIGURATION_SOURCE.GLOBAL }
    : { value: 0, source: null };
};

const resolveBurnRoi = (options = {}) => resolveBurnRoiDetails(options).value;

const resolveApplicableRewardRoi = ({
  journeyType,
  cardMasterConfiguration,
  selectedCard,
  lmsConfiguration,
  globalConfiguration,
} = {}) => {
  const earnResolution = resolveEarnRoiDetails({
    journeyType,
    cardMasterConfiguration,
    selectedCard,
    lmsConfiguration,
    globalConfiguration,
  });
  const burnResolution = resolveBurnRoiDetails({
    journeyType,
    cardMasterConfiguration,
    selectedCard,
    lmsConfiguration,
    globalConfiguration,
  });

  return {
    earnRoi: earnResolution.value,
    burnRoi: burnResolution.value,
    configurationSource: {
      earnRoi: earnResolution.source,
      burnRoi: burnResolution.source,
    },
  };
};

const getLmsRewardCards = (lmsSource) => {
  const cards = lmsSource?.otherListAttribute?.[0]?.valueDetails;
  return Array.isArray(cards)
    ? cards.filter((card) => card && card?.status !== "0")
    : [];
};

const resolveLmsRewardCard = ({
  selectedCard,
  selectedIdentifier,
  lmsSources = [],
} = {}) => {
  if (selectedCard) return selectedCard;

  const sources = Array.isArray(lmsSources) ? lmsSources : [lmsSources];
  const sourceWithCards = sources.find(
    (source) => getLmsRewardCards(source).length > 0,
  );
  const cards = getLmsRewardCards(sourceWithCards);
  if (cards.length === 0) return null;

  const lmsDefaultIdentifier = sources.find(
    (source) => source?.subIdentifier !== undefined &&
      source?.subIdentifier !== null,
  )?.subIdentifier;
  const currentIdentifier = selectedIdentifier ?? lmsDefaultIdentifier;
  const currentCard = currentIdentifier !== undefined && currentIdentifier !== null
    ? cards.find(
      (card) => String(card?.identifier) === String(currentIdentifier),
    )
    : null;
  const defaultCard = lmsDefaultIdentifier !== undefined &&
    lmsDefaultIdentifier !== null
    ? cards.find(
      (card) => String(card?.identifier) === String(lmsDefaultIdentifier),
    )
    : null;

  return currentCard || defaultCard || cards[0];
};

const useEarnRoiCalculationContext = ({
  selectedLmsCard,
  cardMasterConfiguration,
} = {}) => {
  const clientData = useSelector((state) => state.clientDataReducer?.clientData);
  const globalConfiguration = useSelector(
    (state) => state.clientConfigurationReducer?.clientConfigurationData?.[0],
  );
  const cardMasterState = useSelector(
    (state) => state.cardMasterReducer,
  ) || {};
  const cardMasterData = cardMasterState.cardMasterData || [];
  const lmsData = useSelector(
    (state) => state.validateLoginTokenSliceReducer?.data,
  );
  const memberData = useSelector(
    (state) => state.getMemberSliceReducer?.MemberData,
  );
  const currentCardIdentifier = useSelector(
    (state) => state.cardDataSliceReducer?.subIdentifier,
  );
  const journeyType = clientData?.journeyType;
  const storedSelectedCardIdentifier = typeof window !== "undefined"
    ? localStorage.getItem("selectedCardId")
    : null;
  const applicableLmsCard = resolveLmsRewardCard({
    selectedCard: selectedLmsCard,
    selectedIdentifier: storedSelectedCardIdentifier || currentCardIdentifier,
    lmsSources: [lmsData, memberData],
  });
  const cardName = applicableLmsCard?.CardTypeName ??
    applicableLmsCard?.cardTypeName ??
    applicableLmsCard?.cardDetails?.CardTypeName ??
    applicableLmsCard?.cardDetails?.cardTypeName;
  const applicableCardMaster = cardMasterConfiguration ??
    getCardMasterData(cardName, cardMasterData);
  const cardMasterIsLoading = normalizeJourneyType(journeyType) ===
      REWARD_JOURNEY_TYPE.CARD && cardMasterState.isgetLoading === true;
  const globalSources = {
    ...clientData,
    ...globalConfiguration,
    configurations: [
      ...(Array.isArray(globalConfiguration?.clientConfig)
        ? globalConfiguration.clientConfig
        : []),
      ...(Array.isArray(clientData?.clientConfigurations)
        ? clientData.clientConfigurations
        : []),
    ],
  };

  return {
    journeyType,
    cardMasterConfiguration: applicableCardMaster,
    // LMS is a true fallback only after Card Master resolution has completed.
    lmsConfiguration: cardMasterIsLoading ? null : applicableLmsCard,
    globalConfiguration: globalSources,
  };
};

const deriveBurnRoiFromLegacyConfig = ({
  minBurnPoints = 0,
  burnBaseDenomination = 0,
} = {}) => {
  const normalizedBurnBase = Math.max(0, toRewardNumber(burnBaseDenomination));
  const normalizedMinBurnPoints = Math.max(0, toRewardNumber(minBurnPoints));

  if (normalizedBurnBase <= 0 || normalizedMinBurnPoints <= 0) return 0;
  return normalizedBurnBase / normalizedMinBurnPoints;
};

const applyBurnPointRounding = (value, roundMethod, roundDigits) => {
  const numericValue = toRewardNumber(value);
  const digits = Math.max(0, toRewardNumber(roundDigits));
  const factor = 10 ** digits;

  switch (Number(roundMethod)) {
    case 0:
      return numericValue;
    case 1:
      return Math.ceil(numericValue * factor) / factor;
    case 2:
      return Math.floor(numericValue * factor) / factor;
    case 3:
      return Math.sign(numericValue) *
        (Math.round(Math.abs(numericValue) * factor) / factor);
    case 4:
      return Math.trunc(numericValue * factor) / factor;
    case 5:
      return Math.ceil(numericValue);
    default:
      return Math.round(numericValue);
  }
};

const calculateEarnReward = ({
  amount = 0,
  minEarnPoints = 0,
  baseDenomination = 0,
  earnBaseDenomination = baseDenomination,
  discountPercentage = 0,
  displayType = EARNING_DISPLAY_TYPE.VALUE,
} = {}) => {
  const normalizedAmount = Math.max(0, toRewardNumber(amount));
  const normalizedEarnBase = Math.max(0, toRewardNumber(earnBaseDenomination));
  const normalizedMinEarnPoints = Math.max(0, toRewardNumber(minEarnPoints));
  const normalizedDiscount = Math.min(
    100,
    Math.max(0, toRewardNumber(discountPercentage)),
  );

  const eligibleAmount = normalizedAmount * (1 - normalizedDiscount / 100);
  const earnRate = normalizedEarnBase > 0
    ? normalizedMinEarnPoints / normalizedEarnBase
    : 0;
  const percentage = earnRate * 100;
  const points = Math.round(eligibleAmount * earnRate);

  return {
    displayValue:
      displayType === EARNING_DISPLAY_TYPE.PERCENTAGE
        ? percentage
        : points,
    percentage,
    points,
    eligibleAmount,
    earnRate,
  };
};

const calculateBurnReward = ({
  amount = 0,
  points = 0,
  burnRoi = 0,
  minBurnPoints = 0,
  baseDenomination = 0,
  burnBaseDenomination = baseDenomination,
  allowLegacySkuConversion = true,
  roundMethod,
  roundDigits,
} = {}) => {
  const normalizedAmount = Math.max(0, toRewardNumber(amount));
  const normalizedPoints = Math.max(0, toRewardNumber(points));
  const normalizedBurnRoi = Math.max(
    0,
    toRewardNumber(burnRoi) || (allowLegacySkuConversion
      ? deriveBurnRoiFromLegacyConfig({
        minBurnPoints,
        burnBaseDenomination,
      })
      : 0),
  );

  const burnRate = normalizedBurnRoi > 0 ? 1 / normalizedBurnRoi : 0;
  const unroundedPointsRequired = normalizedBurnRoi > 0
    ? normalizedAmount / normalizedBurnRoi
    : 0;

  return {
    burnRoi: normalizedBurnRoi,
    burnRate,
    pointsRequired: applyBurnPointRounding(
      unroundedPointsRequired,
      roundMethod,
      roundDigits,
    ),
    unroundedPointsRequired,
    worth: normalizedPoints * normalizedBurnRoi,
  };
};

const getPositiveDenominations = (denominations) => {
  if (Array.isArray(denominations)) {
    return denominations.flatMap(getPositiveDenominations);
  }

  const entries = typeof denominations === "object" && denominations !== null
    ? [denominations]
    : String(denominations ?? "").split(",");

  return entries
    .map((entry) => toRewardNumber(
      typeof entry === "object" && entry !== null
        ? entry.denomination ?? entry.price ?? entry.value
        : entry,
    ))
    .filter((value) => value > 0);
};

const getRewardBaseDenomination = ({
  product,
  eligibleDenominations = [],
  fallbackDenominations = [],
} = {}) => {
  const eligibleValues = getPositiveDenominations(eligibleDenominations);
  if (eligibleValues.length > 0) return Math.min(...eligibleValues);

  const configuredValues = getPositiveDenominations(product?.priceDenominations);
  if (configuredValues.length > 0) return Math.min(...configuredValues);

  const vendorValues = getPositiveDenominations(
    product?.vendorPriceDenominations,
  );
  if (vendorValues.length > 0) return Math.min(...vendorValues);

  const configuredMinimum = toRewardNumber(product?.minPrice);
  if (configuredMinimum > 0) return configuredMinimum;

  const fallbackValues = getPositiveDenominations(fallbackDenominations);
  return fallbackValues.length > 0 ? Math.min(...fallbackValues) : 0;
};

/**
 * Shared Earn calculation used by product list, PDP and checkout.
 *
 * minEarnPoints is configured against earnBaseDenomination.
 * burn conversion and burn ROI are handled independently.
 */
const calculateEarning = ({
  amount = 0,
  minEarnPoints = 0,
  minBurnPoints = 0,
  earnRoi = 0,
  burnRoi = 0,
  journeyType,
  cardMasterConfiguration,
  selectedCard,
  lmsConfiguration,
  globalConfiguration,
  baseDenomination = 0,
  earnBaseDenomination = baseDenomination,
  burnBaseDenomination = baseDenomination,
  discountPercentage = 0,
  displayType = EARNING_DISPLAY_TYPE.VALUE,
  burnRoundMethod,
  burnRoundDigits,
} = {}) => {
  const hasJourneyContext = journeyType !== undefined && journeyType !== null;
  const normalizedJourney = hasJourneyContext
    ? normalizeJourneyType(journeyType)
    : null;
  const resolvedEarnRoi = resolveEarnRoiDetails({
    journeyType,
    cardMasterConfiguration,
    selectedCard,
    lmsConfiguration,
    globalConfiguration,
  });
  const providedEarnRoi = Math.max(0, toRewardNumber(earnRoi));
  const applicableEarnRoi = providedEarnRoi || resolvedEarnRoi.value;
  const earnRoiSource = resolvedEarnRoi.source;
  const resolvedBurnRoi = resolveBurnRoiDetails({
    journeyType,
    cardMasterConfiguration,
    selectedCard,
    lmsConfiguration,
    globalConfiguration,
  });
  const providedBurnRoi = Math.max(0, toRewardNumber(burnRoi));
  const applicableBurnRoi = providedBurnRoi || resolvedBurnRoi.value;
  // Calls without journey context retain the old behavior until their screen is
  // migrated. Once context is provided, SKU conversion is wallet-only.
  const allowLegacySkuConversion = !hasJourneyContext ||
    normalizedJourney === REWARD_JOURNEY_TYPE.WALLET;

  const earnResult = calculateEarnReward({
    amount,
    minEarnPoints,
    baseDenomination,
    earnBaseDenomination,
    discountPercentage,
    displayType,
  });

  const burnResult = calculateBurnReward({
    amount,
    points: earnResult.points,
    burnRoi: applicableBurnRoi,
    minBurnPoints,
    baseDenomination,
    burnBaseDenomination,
    allowLegacySkuConversion,
    roundMethod: burnRoundMethod,
    roundDigits: burnRoundDigits,
  });

  const earnWorth = earnResult.points * applicableEarnRoi;
  const burnWorth = burnResult.worth;
  const skuConversionRoi = allowLegacySkuConversion
    ? deriveBurnRoiFromLegacyConfig({
      minBurnPoints,
      burnBaseDenomination,
    })
    : 0;
  const usesSkuConversion = applicableEarnRoi <= 0 && skuConversionRoi > 0;
  const worth = applicableEarnRoi > 0
    ? earnWorth
    : usesSkuConversion
      ? earnResult.points * skuConversionRoi
      : 0;
  const burnRoiSource = applicableBurnRoi > 0
    ? resolvedBurnRoi.source
    : burnResult.burnRoi > 0 && allowLegacySkuConversion
      ? REWARD_CONFIGURATION_SOURCE.SKU_CONVERSION
      : null;

  return {
    displayValue: earnResult.displayValue,
    percentage: earnResult.percentage,
    points: earnResult.points,
    worth,
    earnWorth,
    burnWorth,
    earnRoi: applicableEarnRoi,
    earnRoiSource,
    worthSource: applicableEarnRoi > 0
      ? earnRoiSource
      : usesSkuConversion
        ? REWARD_CONFIGURATION_SOURCE.SKU_CONVERSION
        : null,
    eligibleAmount: earnResult.eligibleAmount,
    earnRate: earnResult.earnRate,
    burnRate: burnResult.burnRate,
    burnRoi: burnResult.burnRoi,
    burnRoiSource,
    pointsRequired: burnResult.pointsRequired,
    unroundedPointsRequired: burnResult.unroundedPointsRequired,
  };
};

const allZero = (arr) => {
  return arr.every((value) => value === 0);
};

/**
 * Checks whether the selected amount exceeds the configured transaction amount capping.
 *
 * @param {object} params
 * @param {string} params.transactionAmountType  - "DENOM_TOTAL" | "AFTER_DISC" | "PAYABLE"
 * @param {number} params.totalAmountCapping      - cap value from transactionLimit
 * @param {number} params.totalDenominationAmount - sum of (denomination * qty)
 * @param {number} params.totalDenominationAfterDiscountAmount - after discount
 * @param {number} params.payableAmount           - after discount + convenience fee + GST
 * @returns {{ isTransactionLimitExceeded: boolean, hasTransactionLimitValidation: boolean, validationAmount: number }}
 */
const checkTransactionLimit = ({
  transactionAmountType,
  totalAmountCapping,
  totalDenominationAmount,
  totalDenominationAfterDiscountAmount,
  payableAmount,
}) => {
  const capping = Number(totalAmountCapping) || 0;
  const VALID_TYPES = ["DENOM_TOTAL", "AFTER_DISC", "PAYABLE"];
  const hasTransactionLimitValidation =
    capping > 0 && VALID_TYPES.includes(transactionAmountType);

  const validationAmount =
    transactionAmountType === "DENOM_TOTAL"
      ? totalDenominationAmount
      : transactionAmountType === "AFTER_DISC"
        ? totalDenominationAfterDiscountAmount
        : transactionAmountType === "PAYABLE"
          ? payableAmount
          : 0;

  return {
    hasTransactionLimitValidation,
    validationAmount,
    isTransactionLimitExceeded:
      hasTransactionLimitValidation && validationAmount > capping,
  };
};

const getCardMasterData = (selectedCardName, cardMasterData) => {
  if (!selectedCardName || !Array.isArray(cardMasterData)) {
    return null;
  }
  const normalizedSelectedCardName = String(selectedCardName).trim().toLowerCase();
  const matchedCard = cardMasterData.find(
    (card) => String(card?.cardName ?? "").trim().toLowerCase() ===
      normalizedSelectedCardName && card?.enabled,
  );
  return matchedCard || null;
};

const usePoints = () => {
  const pointBalance = useSelector(
    (state) => state.cardDataSliceReducer?.pointBalance
  );
  return pointBalance;
};

const ClientJourneyType = () => {
  return useSelector((state) => state.clientDataReducer?.clientData?.journeyType);
};
export {
  clientType,
  ACCESS_KEY,
  SECRET_KEY,
  GetImageUrl,
  MaxQuan,
  ClientInfo,
  useGaTrackingConfig,
  useClientConfigFlag,
  useClientConfigValue,
  ThemeData,
  ApplyThemeStyles,
  showMaxMessg,
  SupplierCapping,
  Dispatch,
  V3Data,
  LoginV3Data,
  CardBasedRedemptionList,
  OrderData,
  ParseInt,
  GetCurrentDateTime,
  GenerateToken,
  EarnPoint,
  ApiRespCode,
  VendorData,
  GetMemberData,
  GetClientConfigData,
  CardDataSlice,
  EmailData,
  SendSms,
  SendMail,
  Navigate,
  ClientConfigurationData,
  TruncateName,
  RedeemPoint,
  UrlStoreReducer,
  GetOrderCodeReducer,
  handleError,
  cleanHTML,
  LoginReducerData,
  handleKeyDown,
  VTIGER_ENCRYPTION_KEY,
  VTIGER_RSA_PUBLIC_KEY,
  gtag,
  GetProdBudSumData,
  handleLimit,
  OnSwitchAccountSuccess,
  getImageUrl,
  dynamicSuccessEmailColumnChnages,
  getConfigValue,
  getValidDenominations,
  calculateConvenienceFeeGst,
  calculateEarnPoints,
  EARNING_DISPLAY_TYPE,
  REWARD_JOURNEY_TYPE,
  REWARD_CONFIGURATION_SOURCE,
  normalizeJourneyType,
  resolveEarnRoi,
  resolveBurnRoi,
  resolveApplicableRewardRoi,
  getLmsRewardCards,
  resolveLmsRewardCard,
  useEarnRoiCalculationContext,
  calculateEarnReward,
  calculateBurnReward,
  getRewardBaseDenomination,
  calculateEarning,
  allZero,
  usePoints,
  ClientJourneyType,
  TransactionLimit,
  checkTransactionLimit,
  getCardMasterData,
};
